import { getDateTimeInEnglish } from "helpers/dateTimeHelper";
import { UserType } from "types/UserTypes";
import ReportType from "../../ReportType";

/**
 * Formats the MultiselectField data to an array of strings to match the API payload
 * @param data the MultiselectField data
 * @returns An array of strings
 * @example Before: {key1: true, key2: false} -> After: ["key1"]
 */
const formatMultiselectData = (data: { [key: string]: boolean }): string[] =>
  Object.entries(data)
    .filter(([, value]) => value === true)
    .map(([key]) => key);

const generatePayload = (reportType: ReportType, formData: any, userType: UserType) => {
  if (userType === "shipper") return generateShipperPayload(reportType, formData);
  return generateProviderPayload(reportType, formData);
};

const generateShipperPayload = (reportType: ReportType, formData: any) => {
  switch (reportType) {
    case ReportType.QUICK_REQUEST:
      return {
        report_type: "quick",
        start_date: getDateTimeInEnglish(formData.report_date_from, { format: "DD-MM-YYYY" }),
        end_date: getDateTimeInEnglish(formData.report_date_to, { format: "DD-MM-YYYY" }),
        payment_locations: formatMultiselectData(formData.payment_locations),
        freight_modes: formData.freight_modes,
      };
    case ReportType.REQUESTS:
      return {
        report_type: "request",
        start_date: getDateTimeInEnglish(formData.report_date_from, { format: "DD-MM-YYYY" }),
        end_date: getDateTimeInEnglish(formData.report_date_to, { format: "DD-MM-YYYY" }),
        payment_locations: formatMultiselectData(formData.payment_locations),
        freight_modes: formData.freight_modes,
      };
    case ReportType.SHIPMENTS:
      return {
        report_type: Object.values(formData.request_details).some((value) => value === true) ? "custom" : "shipment",
        start_date: getDateTimeInEnglish(formData.report_date_from, { format: "DD-MM-YYYY" }),
        end_date: getDateTimeInEnglish(formData.report_date_to, { format: "DD-MM-YYYY" }),
        group_by: formData.group_by,
        payment_locations: formatMultiselectData(formData.payment_locations),
        freight_modes: formData.freight_modes,
        custom_report_fields: formatMultiselectData(formData.request_details),
      };
    case ReportType.QUOTES:
      return {
        report_type: "quotation",
        payment_locations: formatMultiselectData(formData.payment_locations),
        freight_modes: formData.freight_modes,
        start_date: getDateTimeInEnglish(formData.report_date_from, { format: "DD-MM-YYYY" }),
        end_date: getDateTimeInEnglish(formData.report_date_to, { format: "DD-MM-YYYY" }),
        include_not_or_not_yet_quoted: formData.include_not_quoted,
        include_declined: formData.include_declined,
      };
    case ReportType.CHARGECODES:
      return {
        report_type: "charge_codes",
        start_date: getDateTimeInEnglish(formData.report_date_from, { format: "DD-MM-YYYY" }),
        end_date: getDateTimeInEnglish(formData.report_date_to, { format: "DD-MM-YYYY" }),
        payment_locations: formatMultiselectData(formData.payment_locations),
        freight_modes: formData.freight_modes,
      };
    case ReportType.INVOICES:
      return {
        report_type: "invoice",
        start_date: getDateTimeInEnglish(formData.report_date_from, { format: "DD-MM-YYYY" }),
        end_date: getDateTimeInEnglish(formData.report_date_to, { format: "DD-MM-YYYY" }),
        group_by: formData.group_by,
        payment_locations: formatMultiselectData(formData.payment_locations),
        freight_modes: formData.freight_modes,
      };
    default:
      return false;
  }
};

const generateProviderPayload = (reportType: ReportType, formData: any) => {
  switch (reportType) {
    case ReportType.SHIPMENTS:
      return {
        report_type: "shipment",
        start_date: getDateTimeInEnglish(formData.report_date_from, { format: "DD-MM-YYYY" }),
        end_date: getDateTimeInEnglish(formData.report_date_to, { format: "DD-MM-YYYY" }),
        group_by: formData.group_by,
        teams: formatMultiselectData(formData.teams),
        freight_modes: formData.freight_modes,
        included_shippers: formatMultiselectData(formData.shippers),
      };
    case ReportType.QUOTES:
      return {
        report_type: "quotation",
        start_date: getDateTimeInEnglish(formData.report_date_from, { format: "DD-MM-YYYY" }),
        end_date: getDateTimeInEnglish(formData.report_date_to, { format: "DD-MM-YYYY" }),
        teams: formatMultiselectData(formData.teams),
        freight_modes: formData.freight_modes,
        included_shippers: formatMultiselectData(formData.shippers),
      };
    case ReportType.INVOICES:
      return {
        report_type: "invoice",
        start_date: getDateTimeInEnglish(formData.report_date_from, { format: "DD-MM-YYYY" }),
        end_date: getDateTimeInEnglish(formData.report_date_to, { format: "DD-MM-YYYY" }),
        group_by: formData.group_by,
        teams: formatMultiselectData(formData.teams),
        freight_modes: formData.freight_modes,
        included_shippers: [],
      };
    default:
      return false;
  }
};

export default generatePayload;
